import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { combineLatest, map } from 'rxjs';
import { NgIf } from '@angular/common';

import {
  AvatarComponent,
  BreadcrumbRouterComponent,
  BreadcrumbRouterService,
  ColorModeService,
  ContainerComponent,
  DropdownComponent,
  DropdownHeaderDirective,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  HeaderComponent,
  HeaderNavComponent,
  HeaderTogglerDirective,
  SidebarToggleDirective,
  BadgeComponent,
} from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { KeycloakService } from '../../../auth/keycloak.service';
import { NotificationService } from '../../../services/notification.service';
import { AppLang, LanguageService } from '../../../i18n';

@Component({
  selector: 'app-default-header',
  templateUrl: './default-header.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, CommonModule, ContainerComponent, HeaderTogglerDirective, SidebarToggleDirective, IconDirective, HeaderNavComponent, RouterLink, NgTemplateOutlet, BreadcrumbRouterComponent, DropdownComponent, DropdownToggleDirective, AvatarComponent, DropdownMenuDirective, DropdownHeaderDirective, DropdownItemDirective, BadgeComponent, TranslocoPipe]
})
export class DefaultHeaderComponent extends HeaderComponent implements OnInit {

  readonly #colorModeService = inject(ColorModeService);
  readonly #notificationService = inject(NotificationService);
  readonly colorMode = this.#colorModeService.colorMode;
  readonly pendingTicketCount = signal(0);
  readonly language = inject(LanguageService);
  readonly currentLang = computed(() => this.language.lang());

  // Route `data.title` may be a translation key or plain text until each area is migrated.
  readonly breadcrumbs = toSignal(
    combineLatest([inject(BreadcrumbRouterService).breadcrumbs$, this.language.translationsChanged$]).pipe(
      map(([items]) => items.map((item) => ({ ...item, label: this.language.label(item.label) })))
    ),
    { initialValue: [] }
  );

  readonly colorModes = [
    { name: 'light', text: marker('header.theme.light'), icon: 'cilSun' },
    { name: 'dark', text: marker('header.theme.dark'), icon: 'cilMoon' },
    { name: 'auto', text: marker('header.theme.auto'), icon: 'cilContrast' }
  ];

  readonly icons = computed(() => {
    const currentMode = this.colorMode();
    return this.colorModes.find(mode => mode.name === currentMode)?.icon ?? 'cilSun';
  });

  constructor(private keycloakService: KeycloakService) {
    super();
  }

  ngOnInit(): void {
    if (this.keycloakService.isMasterAdministrator()) return;
    this.#notificationService.getPendingTicketCount$().subscribe((count) => {
      this.pendingTicketCount.set(count);
    });
  }

  setLanguage(lang: AppLang): void {
    this.language.setLanguage(lang);
  }

  logout() {
    this.keycloakService.logout();
  }

  sidebarId = input('sidebar1');
}
